import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Event } from '../../models/event/event';
import { eventService } from '../../services/event-service';
import { NavigationUtil } from '../../utils/navigation-util';
import { DateUtil } from '../../utils/date-util';

interface CalendarViewProps {
  events: Event[];
}

type CalendarViewMode = 'day' | 'week' | 'month' | 'year';

export const CalendarView: React.FC<CalendarViewProps> = ({ events }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';

  const [viewMode, setViewMode] = useState<CalendarViewMode>('month');
  const [currentDate, setCurrentDate] = useState(new Date());

  // Navigation functions
  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);

    switch (viewMode) {
      case 'day':
        newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
        break;
      case 'week':
        newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
        break;
      case 'month':
        newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
        break;
      case 'year':
        newDate.setFullYear(newDate.getFullYear() + (direction === 'next' ? 1 : -1));
        break;
    }

    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleEventClick = (event: Event) => {
    NavigationUtil.navigateToEvent(event, navigate);
  };

  // Get events for a specific date (including multi-day events)
  const getEventsForDate = (date: Date) => {
    return events.filter(event => {
      const eventStartDate = DateUtil.parseDateString(event.startDate);
      const eventEndDate = DateUtil.parseDateString(event.endDate);

      // Set time to start of day for accurate comparison
      const checkDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const startDate = new Date(eventStartDate.getFullYear(), eventStartDate.getMonth(), eventStartDate.getDate());
      const endDate = new Date(eventEndDate.getFullYear(), eventEndDate.getMonth(), eventEndDate.getDate());

      // Check if the date falls within the event's date range (inclusive)
      return checkDate >= startDate && checkDate <= endDate;
    });
  };

  // Check if a date is the first day of an event
  const isEventStartDate = (event: Event, date: Date) => {
    const eventStartDate = DateUtil.parseDateString(event.startDate);
    const checkDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const startDate = new Date(eventStartDate.getFullYear(), eventStartDate.getMonth(), eventStartDate.getDate());
    return checkDate.getTime() === startDate.getTime();
  };

  // Check if an event spans multiple days
  const isMultiDayEvent = (event: Event) => {
    const eventStartDate = DateUtil.parseDateString(event.startDate);
    const eventEndDate = DateUtil.parseDateString(event.endDate);
    const startDate = new Date(eventStartDate.getFullYear(), eventStartDate.getMonth(), eventStartDate.getDate());
    const endDate = new Date(eventEndDate.getFullYear(), eventEndDate.getMonth(), eventEndDate.getDate());
    return endDate.getTime() > startDate.getTime();
  };

  // Render different calendar views
  const renderDayView = () => {
    const dayEvents = getEventsForDate(currentDate);

    return (
      <div className="bg-white rounded-lg border shadow-sm">
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-3 sm:p-6 border-b">
          <div className="flex items-center justify-between">
            <h4 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
              {currentDate.toLocaleDateString(i18n.language, {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </h4>
            <span className="px-2 sm:px-3 py-1 bg-black text-white text-xs sm:text-sm rounded-full font-medium whitespace-nowrap">
              {dayEvents.length} {dayEvents.length === 1 ? t('pages.events.title').slice(0, -1) : t('pages.events.title')}
            </span>
          </div>
        </div>

        <div className="p-3 sm:p-6">
          {dayEvents.length > 0 ? (
            <div className="space-y-3 sm:space-y-4">
              {dayEvents.map(event => {
                const isOngoing = eventService.isEventOngoing(event);
                const isUpcoming = eventService.isEventUpcoming(event);
                const isMultiDay = isMultiDayEvent(event);

                return (
                  <div
                    key={event.id}
                    onClick={() => handleEventClick(event)}
                    className="group relative overflow-hidden rounded-xl hover:bg-gray-50 cursor-pointer transition-all duration-300 shadow-lg hover:shadow-2xl border border-gray-200"
                  >
                    {/* Event Header with Logo/Cover - Mobile Optimized */}
                    <div className="relative h-16 sm:h-24 bg-gradient-to-br from-gray-900 via-gray-800 to-black overflow-hidden">
                      {/* Background Image */}
                      {event.media?.cover?.url || event.media?.logo?.url ? (
                        <img
                          src={event.media?.cover?.url || event.media?.logo?.url}
                          alt={event.title}
                          className="absolute inset-0 w-full h-full object-cover opacity-30"
                        />
                      ) : null}

                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>

                      {/* Logo Icon - Mobile Responsive */}
                      {event.media?.logo?.url && (
                        <div className="absolute top-2 sm:top-3 left-2 sm:left-3 w-8 h-8 sm:w-12 sm:h-12 bg-white/10 backdrop-blur-md rounded-lg border border-white/30 overflow-hidden shadow-xl">
                          <img
                            src={event.media.logo.url}
                            alt=""
                            className="w-full h-full object-contain p-1 sm:p-1.5"
                          />
                        </div>
                      )}

                      {/* Status Badge - Mobile Responsive */}
                      <div className={`absolute top-2 sm:top-3 right-2 sm:right-3 px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-bold backdrop-blur-md ${
                        isOngoing
                          ? 'bg-green-500/90 text-white shadow-lg shadow-green-500/50 animate-pulse'
                          : isUpcoming
                            ? 'bg-blue-500/90 text-white shadow-lg shadow-blue-500/50'
                            : 'bg-gray-500/90 text-white'
                      }`}>
                        {isOngoing ? t('pages.events.status.ongoing') : isUpcoming ? t('pages.events.status.upcoming') : t('pages.events.status.completed')}
                      </div>

                      {/* Event Title - Mobile Responsive */}
                      <div className="absolute bottom-2 sm:bottom-3 left-2 sm:left-3 right-2 sm:right-3">
                        <h5 className="text-white text-sm sm:text-lg font-bold drop-shadow-lg line-clamp-2">{event.title}</h5>
                      </div>
                    </div>

                    {/* Event Details - Mobile Optimized */}
                    <div className="p-3 sm:p-4">
                      <div className="flex items-center justify-between mb-2 sm:mb-3 flex-wrap gap-2">
                        <div className="flex items-center space-x-1 sm:space-x-2">
                          {isMultiDay && (
                            <span className="px-2 sm:px-3 py-1 text-[10px] sm:text-xs rounded-full font-bold bg-purple-100 text-purple-800 border border-purple-200 whitespace-nowrap">
                              {DateUtil.parseDateString(event.startDate).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })} - {DateUtil.parseDateString(event.endDate).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })}
                            </span>
                          )}
                        </div>
                        <span className="px-2 sm:px-3 py-1 bg-black text-white text-[10px] sm:text-xs rounded-lg font-bold shadow-md whitespace-nowrap">
                          {t(`pages.events.categories.${event.category}`)}
                        </span>
                      </div>

                      <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm text-gray-600">
                        <div className="flex items-center">
                          <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-100 rounded-lg flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                            <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <span className="font-medium">{event.startTime} - {event.endTime}</span>
                        </div>
                        <div className="flex items-center">
                          <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-100 rounded-lg flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                            <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            </svg>
                          </div>
                          <span className="truncate">{event.address.name}</span>
                        </div>
                      </div>

                      {event.shortDescription && (
                        <p className="text-xs sm:text-sm text-gray-700 mt-3 sm:mt-4 line-clamp-2 sm:line-clamp-2 leading-relaxed">
                          {event.shortDescription}
                        </p>
                      )}
                    </div>

                    {/* Hover Effect */}
                    <div className="absolute inset-0 border-2 border-transparent group-hover:border-black/20 rounded-xl transition-all duration-300 pointer-events-none"></div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2v12a2 2 0 002 2z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('pages.events.noEvents')}</h3>
              <p className="text-gray-500">{t('pages.events.noEventsDescription')}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());

    const weekDays = Array.from({ length: 7 }, (_, i) => {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      return day;
    });

    return (
      <div className="bg-white rounded-lg border overflow-hidden shadow-lg">
        {/* Week header */}
        <div className="grid grid-cols-7 border-b bg-gradient-to-b from-gray-50 to-white">
          {weekDays.map((day, index) => (
            <div key={index} className="p-4 text-center border-r last:border-r-0">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                {day.toLocaleDateString(i18n.language, { weekday: 'short' })}
              </div>
              <div className={`text-xl font-bold mt-1 ${
                day.toDateString() === new Date().toDateString()
                  ? 'bg-black text-white rounded-full w-10 h-10 flex items-center justify-center mx-auto'
                  : 'text-gray-900'
              }`}>
                {day.getDate()}
              </div>
            </div>
          ))}
        </div>

        {/* Week content - Mobile Responsive */}
        <div className="grid grid-cols-7 min-h-[200px] sm:min-h-[300px]">
          {weekDays.map((day, index) => {
            const dayEvents = getEventsForDate(day);
            return (
              <div key={index} className="p-1 sm:p-2 border-r last:border-r-0 min-h-[200px] sm:min-h-[300px] bg-gray-50/30">
                <div className="space-y-1 sm:space-y-2">
                  {dayEvents.slice(0, window.innerWidth < 640 ? 1 : 2).map(event => {
                    const isFirstDay = isEventStartDate(event, day);
                    const isMultiDay = isMultiDayEvent(event);
                    const isLastDay = isMultiDay && DateUtil.parseDateString(event.endDate).toDateString() === day.toDateString();

                    // Use cover photo if available, otherwise fallback to logo
                    const coverImageUrl = event.media?.cover?.url || event.media?.logo?.url;

                    // Determine border radius for multi-day spanning
                    let borderRadiusClass = "rounded-lg";
                    if (isMultiDay) {
                      if (isFirstDay && !isLastDay) {
                        borderRadiusClass = isRTL ? "rounded-l-lg rounded-r-none" : "rounded-l-lg rounded-r-none";
                      } else if (isLastDay && !isFirstDay) {
                        borderRadiusClass = isRTL ? "rounded-r-lg rounded-l-none" : "rounded-r-lg rounded-l-none";
                      } else if (!isFirstDay && !isLastDay) {
                        borderRadiusClass = "rounded-none";
                      }
                    }

                    return (
                      <div
                        key={event.id}
                        onClick={() => handleEventClick(event)}
                        className="group relative overflow-hidden cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-xl"
                        title={`${event.title} - ${event.startTime}${isMultiDay ? ` (${DateUtil.parseDateString(event.startDate).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })} - ${DateUtil.parseDateString(event.endDate).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })})` : ''}`}
                      >
                        {/* Cover Photo spanning across days - Mobile Responsive */}
                        <div className={`relative h-8 sm:h-16 w-full bg-gray-100 overflow-hidden shadow-sm ${borderRadiusClass}`}>
                          {coverImageUrl ? (
                            <img
                              src={coverImageUrl}
                              alt={event.title}
                              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                            />
                          ) : (
                            /* Fallback for events without cover photo - Mobile Optimized */
                            <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                              <svg className="w-3 h-3 sm:w-6 sm:h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                          
                          {/* Event title overlay on hover - Mobile Optimized */}
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                            <div className="p-1 sm:p-2 w-full">
                              <div className="text-white text-[8px] sm:text-[10px] font-bold leading-tight truncate">
                                {event.title}
                              </div>
                              <div className="text-white/80 text-[6px] sm:text-[8px] truncate">
                                {event.startTime}
                              </div>
                            </div>
                          </div>

                          {/* Event title for multi-day - Mobile Optimized */}
                          {isMultiDay && isFirstDay && (
                            <div className="absolute top-0.5 sm:top-1 left-0.5 sm:left-1 bg-black/80 text-white text-[5px] sm:text-[6px] px-0.5 sm:px-1 py-0.5 rounded-sm font-medium max-w-[80%] truncate">
                              {event.title}
                            </div>
                          )}

                          {/* Multi-day continuation indicators */}
                          {isMultiDay && !isFirstDay && !isLastDay && (
                            <div className="absolute inset-y-0 left-0 w-0.5 bg-white/30"></div>
                          )}
                          {isMultiDay && !isFirstDay && !isLastDay && (
                            <div className="absolute inset-y-0 right-0 w-0.5 bg-white/30"></div>
                          )}

                          {/* End day indicator */}
                          {isMultiDay && isLastDay && (
                            <div className="absolute top-0.5 sm:top-1 right-0.5 sm:right-1 bg-black/80 text-white text-[5px] sm:text-[6px] px-0.5 sm:px-1 py-0.5 rounded-sm font-medium">
                              End
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {dayEvents.length > (window.innerWidth < 640 ? 1 : 2) && (
                    <div className="bg-gradient-to-r from-gray-700 to-gray-800 text-white text-[6px] sm:text-[8px] font-medium py-0.5 sm:py-1 px-1 sm:px-2 rounded-sm sm:rounded-md cursor-pointer hover:from-gray-600 hover:to-gray-700 transition-all duration-300 text-center shadow-sm">
                      +{dayEvents.length - (window.innerWidth < 640 ? 1 : 2)} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderMonthView = () => {
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
    const firstDayOfWeek = firstDayOfMonth.getDay();
    const daysInMonth = lastDayOfMonth.getDate();

    // Generate calendar days
    const calendarDays: (Date | null)[] = [];

    // Add empty cells for days before the first day of month
    for (let i = 0; i < firstDayOfWeek; i++) {
      calendarDays.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      calendarDays.push(new Date(currentYear, currentMonth, day));
    }

    // Add empty cells to complete the grid (42 cells total for 6 weeks)
    while (calendarDays.length < 42) {
      calendarDays.push(null);
    }

    const dayNames = isRTL
      ? ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت']
      : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
      <div className="bg-white rounded-lg border overflow-hidden shadow-sm">
        {/* Enhanced Month header */}
        <div className="grid grid-cols-7 bg-gray-50 border-b">
          {dayNames.map((day, index) => (
            <div key={index} className="p-3 text-center text-sm font-semibold text-gray-700 border-r last:border-r-0 bg-gradient-to-b from-gray-50 to-gray-100">
              {day}
            </div>
          ))}
        </div>

        {/* Enhanced Month grid - Mobile Responsive */}
        <div className="grid grid-cols-7">
          {calendarDays.map((date, index) => {
            const dayEvents = date ? getEventsForDate(date) : [];
            const isToday = date &&
              date.getDate() === new Date().getDate() &&
              date.getMonth() === new Date().getMonth() &&
              date.getFullYear() === new Date().getFullYear();
            
            const isPast = date && date < new Date();
            const hasEvents = dayEvents.length > 0;

            return (
              <div
                key={index}
                className={`min-h-[80px] sm:min-h-[140px] p-1 sm:p-3 border-r border-b last:border-r-0 transition-all duration-200 ${
                  date 
                    ? `bg-white hover:bg-gray-50 cursor-pointer ${hasEvents ? 'ring-1 ring-blue-100' : ''} ${isPast ? 'opacity-75' : ''}` 
                    : 'bg-gray-25'
                }`}
              >
                {date && (
                  <>
                    <div
                      className={`text-xs sm:text-sm font-semibold mb-1 sm:mb-2 ${
                        isToday
                          ? 'bg-black text-white rounded-full w-5 h-5 sm:w-7 sm:h-7 flex items-center justify-center text-xs mx-auto'
                          : isPast ? 'text-gray-500' : 'text-gray-900'
                      }`}
                    >
                      {isToday ? (
                        date.getDate()
                      ) : (
                        <span className="flex items-center justify-center w-5 h-5 sm:w-7 sm:h-7 rounded-full hover:bg-gray-100">
                          {date.getDate()}
                        </span>
                      )}
                    </div>

                    {/* Event Cover Photos Spanning Multiple Days - Mobile Optimized */}
                    <div className="space-y-1 sm:space-y-1.5">
                      {dayEvents.slice(0, 1).map((event) => {
                        const isFirstDay = isEventStartDate(event, date);
                        const isMultiDay = isMultiDayEvent(event);
                        const isLastDay = isMultiDay && DateUtil.parseDateString(event.endDate).toDateString() === date.toDateString();
                        
                        // Use cover photo if available, otherwise fallback to logo
                        const coverImageUrl = event.media?.cover?.url || event.media?.logo?.url;

                        // Determine border radius for multi-day spanning
                        let borderRadiusClass = "rounded-lg";
                        if (isMultiDay) {
                          if (isFirstDay && !isLastDay) {
                            borderRadiusClass = isRTL ? "rounded-l-lg rounded-r-none" : "rounded-l-lg rounded-r-none";
                          } else if (isLastDay && !isFirstDay) {
                            borderRadiusClass = isRTL ? "rounded-r-lg rounded-l-none" : "rounded-r-lg rounded-l-none";
                          } else if (!isFirstDay && !isLastDay) {
                            borderRadiusClass = "rounded-none";
                          }
                        }

                        return (
                          <div
                            key={event.id}
                            onClick={() => handleEventClick(event)}
                            className="group relative overflow-hidden cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg"
                            title={`${event.title} - ${event.startTime}${isMultiDay ? ` (${DateUtil.parseDateString(event.startDate).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })} - ${DateUtil.parseDateString(event.endDate).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })})` : ''}`}
                          >
                            {/* Cover Photo spanning across days - Mobile Responsive */}
                            <div className={`relative h-10 sm:h-20 w-full bg-gray-100 overflow-hidden shadow-sm ${borderRadiusClass}`}>
                              {coverImageUrl ? (
                                <img
                                  src={coverImageUrl}
                                  alt={event.title}
                                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                                />
                              ) : (
                                /* Fallback for events without cover photo - Mobile Optimized */
                                <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                                  <svg className="w-4 h-4 sm:w-8 sm:h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2v12a2 2 0 002 2z" />
                                  </svg>
                                </div>
                              )}
                              
                              {/* Event title overlay - Mobile Optimized */}
                              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                                <div className="p-1 sm:p-2 w-full">
                                  <div className="text-white text-[8px] sm:text-[10px] font-bold leading-tight truncate">
                                    {event.title}
                                  </div>
                                  <div className="text-white/80 text-[6px] sm:text-[8px] truncate">
                                    {event.startTime}
                                  </div>
                                </div>
                              </div>

                              {/* Event title for multi-day - Mobile Optimized */}
                              {isMultiDay && isFirstDay && (
                                <div className="absolute top-0.5 sm:top-1 left-0.5 sm:left-1 bg-black/80 text-white text-[6px] sm:text-[7px] px-1 sm:px-1.5 py-0.5 rounded-sm sm:rounded-md font-medium max-w-[80%] truncate">
                                  {event.title}
                                </div>
                              )}

                              {/* Multi-day continuation indicators - Mobile Optimized */}
                              {isMultiDay && !isFirstDay && !isLastDay && (
                                <div className="absolute inset-y-0 left-0 w-0.5 sm:w-1 bg-white/30"></div>
                              )}
                              {isMultiDay && !isFirstDay && !isLastDay && (
                                <div className="absolute inset-y-0 right-0 w-0.5 sm:w-1 bg-white/30"></div>
                              )}

                              {/* End day indicator - Mobile Optimized */}
                              {isMultiDay && isLastDay && (
                                <div className="absolute top-0.5 sm:top-1 right-0.5 sm:right-1 bg-black/80 text-white text-[6px] sm:text-[7px] px-1 sm:px-1.5 py-0.5 rounded-sm sm:rounded-md font-medium">
                                  End
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      {dayEvents.length > 1 && (
                        <div className="bg-gradient-to-r from-gray-700 to-gray-800 text-white text-[6px] sm:text-[8px] font-medium py-0.5 sm:py-1 px-1 sm:px-2 rounded-sm sm:rounded-md cursor-pointer hover:from-gray-600 hover:to-gray-700 transition-all duration-300 text-center shadow-sm">
                          +{dayEvents.length - 1} more
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderYearView = () => {
    const currentYear = currentDate.getFullYear();
    const months = Array.from({ length: 12 }, (_, i) => {
      return new Date(currentYear, i, 1);
    });

    return (
      <div className="bg-white rounded-lg border p-2 sm:p-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-4">
          {months.map((month, index) => {
            const monthEvents = events.filter(event => {
              const eventDate = DateUtil.parseDateString(event.startDate);
              return (
                eventDate.getMonth() === month.getMonth() &&
                eventDate.getFullYear() === month.getFullYear()
              );
            });

            return (
              <div
                key={index}
                className="p-2 sm:p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => {
                  setCurrentDate(month);
                  setViewMode('month');
                }}
              >
                <h4 className="font-medium text-center mb-2 text-sm sm:text-base">
                  {month.toLocaleDateString(i18n.language, { month: 'long' })}
                </h4>

                {monthEvents.length > 0 && (
                  <div className="text-center">
                    <span className="inline-block px-2 py-1 bg-black text-white text-xs rounded">
                      {monthEvents.length} {monthEvents.length === 1 ? t('pages.events.title').slice(0, -1) : t('pages.events.title')}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const getDateDisplayText = () => {
    switch (viewMode) {
      case 'day':
        return currentDate.toLocaleDateString(i18n.language, {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      case 'week':
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return `${startOfWeek.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })} - ${endOfWeek.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' })}`;
      case 'month':
        return currentDate.toLocaleDateString(i18n.language, {
          year: 'numeric',
          month: 'long'
        });
      case 'year':
        return currentDate.getFullYear().toString();
      default:
        return '';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
      {/* Enhanced Calendar Header - Mobile Optimized */}
      <div className="bg-gradient-to-r from-black to-gray-800 text-white p-4 sm:p-6">
        <div className="flex flex-col gap-4">
          {/* Title and Navigation Row */}
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg sm:text-2xl font-bold truncate">
                {getDateDisplayText()}
              </h3>
              <p className="text-gray-300 text-xs sm:text-sm mt-1">
                {events.length} {events.length === 1 ? t('pages.events.title').slice(0, -1) : t('pages.events.title')}
              </p>
            </div>

            {/* Compact Navigation Controls */}
            <div className="flex gap-1 bg-white/10 rounded-lg p-1">
              <button
                onClick={() => navigateDate('prev')}
                className="p-2 hover:bg-white/20 rounded-md transition-colors"
                title={t('navigation.previous')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <button
                onClick={goToToday}
                className="px-2 sm:px-3 py-2 text-xs font-medium bg-white text-black rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                {t('navigation.today')}
              </button>

              <button
                onClick={() => navigateDate('next')}
                className="p-2 hover:bg-white/20 rounded-md transition-colors"
                title={t('navigation.next')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile-Optimized View Mode Selector */}
          <div className="flex bg-white/10 rounded-lg p-1 w-full">
            {(['day', 'week', 'month', 'year'] as CalendarViewMode[]).map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`flex-1 px-2 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 ${
                  viewMode === mode
                    ? 'bg-white text-black shadow-lg'
                    : 'text-white/80 hover:text-white hover:bg-white/20'
                }`}
              >
                {t(`view.${mode}`)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Calendar Content with Mobile-Responsive Padding */}
      <div className="p-3 sm:p-6">
        {viewMode === 'day' && renderDayView()}
        {viewMode === 'week' && renderWeekView()}
        {viewMode === 'month' && renderMonthView()}
        {viewMode === 'year' && renderYearView()}
      </div>
    </div>
  );
};